import { Fragment, useEffect, useState } from "react";
import type { HTMLAttributes } from "react";
import { ArrowLeftRight, ChevronRight, Info, Loader2, X } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Item,
  ItemActions,
  ItemContent,
  ItemDescription,
  ItemGroup,
  ItemMedia,
  ItemTitle,
} from "@/components/ui/item";
import { Skeleton } from "@/components/ui/skeleton";
import { Separator } from "@/components/ui/separator";
import { ProductSearchPicker } from "modules/lists/components/product-search-picker";
import { ProductData } from "modules/product/types/product";
import { ProductDecision } from "modules/checkout/types/productDecission";
import { useFetch } from "modules/main/hooks/useFetch";
import { endPointProductAlternatives } from "modules/main/services/endpoints";
import { CandidateProduct } from "modules/lists/types/lists";
import { CartItem, Payload } from "../types/cart";
import { candidatesToProductData } from "../utils/candidate-utils";
import Amount from "utils/amount";
import { cn } from "@/lib/utils";
import { ProductSheetDialog } from "./product-sheet-dialog";

const VISIBLE_CANDIDATES = 2;

interface FreeItemReplacementSectionProps extends HTMLAttributes<HTMLDivElement> {
  product: CartItem;
  onDecisionConfirmed: (decision: ProductDecision) => void;
  /** Fired with the chosen cart payload when the user replaces the item manually. */
  onManualProductSelected?: (payload: Payload) => void;
  /** Fired when the user swaps an alternative in as the main product.
   *  Receives the promoted candidate, the full candidate list (including
   *  omitted ones, so discards survive the swap) and the still-active
   *  candidates (excludes omitted ones). */
  onCandidatePromoted?: (
    candidate: CandidateProduct,
    candidates: CandidateProduct[],
    activeCandidates: CandidateProduct[],
  ) => void;
  /** Id (sku ?? id) of the candidate currently being swapped in as main product. */
  promotingCandidateId?: string | null;
}

/**
 * Renders the replacement-preference section for AI-resolved (free) cart items.
 *
 * When the item has resolver candidates, it presents them ("De no estar,
 * intentamos con") as a compact list truncated to `VISIBLE_CANDIDATES` items
 * with a "Ver X más" / "Ver menos" toggle. Each row shows the product photo,
 * its name (clickable to open the product detail in a dialog), a swap button
 * (only for AI-resolved items) that promotes the candidate to the main product,
 * and a dismiss "X" button that strikes the candidate through (toggle, it can
 * be reactivated) instead of removing it from the list.
 *
 * When the item has no candidates, it shows "No hay mas opciones de: ..." with
 * an opt-in checkbox ("Si no esta disponible, aceptar otra marca equivalente").
 * Checking it lazily fetches brand equivalents via the alternatives endpoint
 * and renders them with the same list UI. When the endpoint returns no
 * equivalents, it shows an informational message ("Sin alternativas. Si no
 * está disponible, se retira del carrito.") plus a "Buscar manualmente" button
 * that opens `ProductSearchPicker` for a manual replacement.
 *
 * A "Sustituir por defecto" checkbox (checked by default when candidates exist)
 * reflects the current default decision: when checked, the active candidates
 * are selected as replacements (`selected-replacement`); when unchecked, no
 * replacement is applied (`no-replacement`). Omitting the last active candidate
 * confirms `no-replacement`, keeping every row visible.
 *
 * @param props - The cart item, a callback to persist the chosen decision and
 * an optional handler for manual replacement selection.
 * @returns The considered-alternatives (or on-demand equivalents) list.
 */
export const FreeItemReplacementSection = ({
  product,
  onDecisionConfirmed,
  onManualProductSelected,
  onCandidatePromoted,
  promotingCandidateId = null,
  ...props
}: FreeItemReplacementSectionProps) => {
  const [expanded, setExpanded] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  const rawCandidates = product.alternativeCandidates ?? [];
  const alternatives: ProductData[] = candidatesToProductData(rawCandidates);
  const hasCandidates = alternatives.length > 0;
  const isAiResolved = product.is_ai_resolved === true;
  const decision = product.productDecision;

  const [omittedIds, setOmittedIds] = useState<Set<string>>(() => {
    if (decision?.action === "selected-replacement") {
      return new Set(
        alternatives
          .map((alt) => alt.price_detail.best_price_id)
          .filter((id) => !decision.replacementIds.includes(id)),
      );
    }
    return new Set();
  });

  const activeCandidates = rawCandidates.filter(
    (candidate) => !omittedIds.has(candidate.sku ?? candidate.id),
  );
  const candidateByBestPriceId = new Map(
    rawCandidates.map((candidate) => [
      candidate.sku ?? candidate.id,
      candidate,
    ]),
  );

  const [substituteByDefault, setSubstituteByDefault] = useState(
    hasCandidates
      ? decision?.action !== "no-replacement"
      : decision?.action === "selected-replacement",
  );

  const sku = product.price_component[0]?.sku ?? null;
  const equivalentsUrl =
    !hasCandidates && substituteByDefault && sku
      ? endPointProductAlternatives(sku, true)
      : null;
  const { data: equivalentsData, loading: equivalentsLoading } =
    useFetch<ProductData[]>(equivalentsUrl);

  const noAlternatives =
    !hasCandidates &&
    substituteByDefault &&
    !equivalentsLoading &&
    equivalentsData !== null &&
    equivalentsData.length === 0;

  const listItems = hasCandidates ? alternatives : (equivalentsData ?? []);
  const hiddenCount = Math.max(0, listItems.length - VISIBLE_CANDIDATES);
  const displayedAlternatives = expanded
    ? listItems
    : listItems.slice(0, VISIBLE_CANDIDATES);

  const emitDecision = (
    nextOmitted: Set<string>,
    substitute: boolean,
    items: ProductData[],
  ) => {
    const remaining = items.filter(
      (alt) => !nextOmitted.has(alt.price_detail.best_price_id),
    );
    if (!substitute || remaining.length === 0) {
      onDecisionConfirmed({
        productId: product.id,
        action: "no-replacement",
        replacementIds: [],
      });
    } else {
      onDecisionConfirmed({
        productId: product.id,
        action: "selected-replacement",
        replacementIds: remaining.map((alt) => alt.price_detail.best_price_id),
      });
    }
  };

  useEffect(() => {
    if (
      !hasCandidates &&
      substituteByDefault &&
      equivalentsData &&
      equivalentsData.length > 0
    ) {
      emitDecision(omittedIds, true, equivalentsData);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasCandidates, substituteByDefault, equivalentsData]);

  const handleOmit = (alt: ProductData) => {
    const nextOmitted = new Set(omittedIds);
    const id = alt.price_detail.best_price_id;
    if (nextOmitted.has(id)) {
      nextOmitted.delete(id);
    } else {
      nextOmitted.add(id);
    }
    setOmittedIds(nextOmitted);
    emitDecision(nextOmitted, substituteByDefault, listItems);
  };

  const handleSubstituteToggle = (checked: boolean) => {
    setSubstituteByDefault(checked);
    if (checked && listItems.length === 0) {
      return;
    }
    emitDecision(omittedIds, checked, listItems);
  };

  const renderItem = (alt: ProductData) => {
    const imageUrl = alt.stores[0]?.images?.catalog ?? alt.stores[0]?.image_url;
    const name = alt.stores[0]?.name ?? alt.brand;
    const isOmitted = omittedIds.has(alt.price_detail.best_price_id);
    const candidate = candidateByBestPriceId.get(
      alt.price_detail.best_price_id,
    );
    const canSwap =
      isAiResolved && hasCandidates && !!candidate && !isOmitted;
    return (
      <Fragment key={alt.id}>
        <Item
          variant="default"
          size="xs"
          className="w-full min-w-0 flex-nowrap px-0 py-1"
        >
          <ItemMedia className="shrink-0">
            <img
              className="size-8 rounded-sm object-contain"
              src={imageUrl}
              alt={name}
            />
          </ItemMedia>
          <ItemContent className="min-w-0 flex-1 basis-0 overflow-hidden gap-0">
            <ItemTitle className="min-w-0 text-xxs">
              <ProductSheetDialog productID={alt.slug} ariaLabel={name}>
                <button
                  type="button"
                  className="flex min-w-0 items-center gap-1 text-left font-semibold"
                >
                  <span
                    className={cn(
                      "min-w-0 flex-1 truncate",
                      isOmitted && "line-through text-muted-foreground",
                    )}
                  >
                    {name}
                  </span>
                  <ChevronRight
                    className={cn(
                      "size-3 shrink-0 text-muted-foreground",
                      isOmitted && "text-muted-foreground",
                    )}
                  />
                </button>
              </ProductSheetDialog>
            </ItemTitle>
            <ItemDescription className="min-w-0">
              {alt.brand && alt.brand !== name && (
                <span className="block truncate text-xxs">{alt.brand}</span>
              )}
              <Amount
                amount={alt.price_detail.best_price}
                currency="CLP"
                locale="es-CL"
                className={cn(
                  "text-xxs font-semibold",
                  isOmitted && "text-muted-foreground",
                )}
              />
            </ItemDescription>
          </ItemContent>
          <ItemActions className="shrink-0">
            {canSwap && (
              <button
                type="button"
                onClick={() =>
                  onCandidatePromoted?.(
                    candidate!,
                    rawCandidates,
                    activeCandidates,
                  )
                }
                disabled={promotingCandidateId !== null}
                aria-label={`Intercambiar ${name}`}
                title="Intercambiar"
                className="rounded-md p-1 text-muted-foreground hover:text-primary hover:bg-muted cursor-pointer transition-colors disabled:opacity-50 disabled:pointer-events-none"
              >
                {promotingCandidateId === (candidate?.sku ?? candidate?.id) ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <ArrowLeftRight className="h-4 w-4" />
                )}
              </button>
            )}
            <button
              type="button"
              onClick={() => handleOmit(alt)}
              aria-label={isOmitted ? `Reactivar ${name}` : `Omitir ${name}`}
              className={cn(
                "rounded-md p-1 text-muted-foreground hover:text-destructive hover:bg-muted cursor-pointer transition-colors",
                isOmitted && "text-primary hover:text-destructive",
              )}
            >
              <X className="h-4 w-4" />
            </button>
          </ItemActions>
        </Item>
      </Fragment>
    );
  };

  const itemList = (
    <ItemGroup
      className={cn(
        "w-full min-w-0 transition-opacity duration-200 gap-2",
        !substituteByDefault && "opacity-50 pointer-events-none grayscale",
      )}
    >
      {displayedAlternatives.map(renderItem)}
    </ItemGroup>
  );

  const showMoreToggle = hiddenCount > 0 && (
    <Button
      variant="ghost"
      size="xs"
      className="w-full text-xxs"
      onClick={() => setExpanded((prev) => !prev)}
    >
      {expanded ? "Ver menos" : `Ver ${hiddenCount} más`}
    </Button>
  );

  return (
    <div className="flex flex-col gap-2 w-full min-w-0" {...props}>
      <Separator className="my-2" />
      {hasCandidates ? (
        <div className="flex flex-col gap-2 w-full">
          <div className="flex items-center justify-between gap-2 text-muted-foreground text-xxs">
            <span className="font-semibold">De no estar, intentamos con</span>
            <label
              htmlFor={`substitute-by-default-${product.id}`}
              className="flex items-center gap-2 text-xxs font-medium cursor-pointer"
            >
              <Checkbox
                id={`substitute-by-default-${product.id}`}
                checked={substituteByDefault}
                onCheckedChange={(checked) => handleSubstituteToggle(!!checked)}
              />
              Sustituir por defecto
            </label>
          </div>
          {itemList}
          {showMoreToggle}
        </div>
      ) : (
        <div className="flex flex-col gap-2 w-full">
          {isAiResolved && (
            <span className="text-muted-foreground text-xxs font-semibold">
              No hay mas opciones de: {product.raw_description ?? product.name}
            </span>
          )}
          <label
            htmlFor={`accept-equivalent-${product.id}`}
            className="flex items-center gap-2 text-xxs font-medium text-muted-foreground cursor-pointer"
          >
            <Checkbox
              id={`accept-equivalent-${product.id}`}
              checked={substituteByDefault}
              onCheckedChange={(checked) => handleSubstituteToggle(!!checked)}
            />
            Si no esta disponible, aceptar otra marca equivalente
          </label>
          {substituteByDefault &&
            (equivalentsLoading ? (
              <div className="flex flex-col gap-2 w-full">
                {[1, 2].map((i) => (
                  <Skeleton key={i} className="h-10 w-full" />
                ))}
              </div>
            ) : noAlternatives ? (
              <div className="flex flex-col gap-2 w-full">
                <div className="flex items-start gap-2 text-xxs text-muted-foreground">
                  <Info
                    data-testid="no-alternatives-info"
                    className="mt-0.5 h-4 w-4 shrink-0"
                  />
                  <span>
                    Sin alternativas. Si no está disponible, se retira del
                    carrito.
                  </span>
                </div>
                <Button
                  variant="secondary"
                  size="xs"
                  className="w-full text-xxs"
                  onClick={() => setPickerOpen(true)}
                >
                  Buscar manualmente
                </Button>
              </div>
            ) : (
              <>
                {itemList}
                {showMoreToggle}
              </>
            ))}
        </div>
      )}
      {pickerOpen && (
        <ProductSearchPicker
          open={pickerOpen}
          onOpenChange={setPickerOpen}
          initialQuery={product.raw_description || product.name}
          onProductSelected={(payload) => onManualProductSelected?.(payload)}
        />
      )}
    </div>
  );
};
